// Sidebar.tsx
import React from "react";
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {Menu as MenuIcon, SupervisorAccount as SupervisorAccountIcon} from "@mui/icons-material";
import {menuIcons, MenuItem} from "../../pages/App";
import theme from "../../theme";
import medendoLogo from "../../assets/medendo_logo.png";
import {useAuthStore} from "../../store/authStore";
import LogoutIcon from '@mui/icons-material/Logout';
import {useAppStore} from "../../store/appStore";
import {Role} from "../../models/UserModel";
import {hasRole} from "../../utils/roles";

interface SidebarProps {
    drawerWidth: number;
    onMenuSelect: (menu: MenuItem) => void;
    handleLogout: () => void;
    open: boolean;
    onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
                                                    drawerWidth,
                                                    onMenuSelect,
                                                    handleLogout,
                                                    open,
                                                    onToggle,
                                                }) => {
    const muiTheme = useTheme();
    const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up("md"));
    const payload = useAuthStore(state => state.payload);
    const userModel = useAppStore((state) => state.userModel);
    const hasTemplateAdmin = hasRole(userModel?.roles, Role.TEMPLATE_ADMIN)
    const isIntegration = useAppStore((state) => state.isIntegration);

    // Width for collapsed sidebar (just enough for the toggle button)
    const collapsedWidth = 0;

    return (
        <>
            {!open && !isIntegration && (
                <IconButton
                    color="inherit"
                    aria-label="toggle sidebar"
                    onClick={onToggle}
                    edge="start"
                    sx={{
                        width: 32,
                        height: 32,
                        position: "sticky",
                        top: 15,
                        left: 10,
                        zIndex: 1300,
                        backgroundColor: "white",
                        borderStyle: "none",
                        boxShadow: 0,
                        transition: theme.transitions.create(["left"], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                        "&:hover": {
                            backgroundColor: "#e0e0e0",
                        },
                    }}
                >
                    <MenuIcon/>
                </IconButton>
            )}
            <Drawer
                sx={{
                    width: open ? drawerWidth : collapsedWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: open ? drawerWidth : collapsedWidth,
                        boxSizing: "border-box",
                        borderRadius: '0',
                        padding: "0",
                        backgroundColor: "#F6F9FE",
                        overflowX: "hidden",
                        transition: theme.transitions.create(['width'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    },
                }}
                variant={isLargeScreen ? "permanent" : "temporary"}
                anchor="left"
                open={isLargeScreen ? true : open}
                onClose={!isLargeScreen ? onToggle : undefined}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile
                }}
            >
                <Box sx={{
                    display: (isLargeScreen && !open) ? "none" : "block",
                }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            backgroundColor: "#F6F9FE",
                            ...theme.mixins.toolbar,
                        }}
                    >
                        <img src={medendoLogo} alt="Logo" width={180}/>
                    </Box>
                    <List>
                        {Object.values(MenuItem).map((text) => (
                            <ListItem key={text} disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        onMenuSelect(text);
                                        // Auto close sidebar on mobile after selection
                                        if (!isLargeScreen) {
                                            onToggle();
                                        }
                                    }}
                                >
                                    {menuIcons[text]}
                                    <ListItemText primary={text}/>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                        <Divider/>
                        <List>
                            <ListItem key="Logout" disablePadding>
                                <ListItemButton onClick={handleLogout}>
                                    <LogoutIcon sx={{mr: '8px'}}/>
                                    <ListItemText primary="Logout"/>
                                </ListItemButton>
                            </ListItem>
                        </List>
                        <Divider/>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mt={3}>
                            {hasTemplateAdmin && <SupervisorAccountIcon fontSize="small"/>}
                            <Typography variant="body2">
                                {payload?.email}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="caption"
                            align="right"
                            display="block"
                            sx={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                            }}
                        >
                            Version: {process.env.REACT_APP_VERSION ?? 'dev'}
                        </Typography>
                    </Box>
            </Drawer>
        </>
    );
};
